import { indexOf, orderBy } from 'lodash';
import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { UseQueryResult } from 'react-query';

import { DataGridColumnDefinition, CrudPage } from 'components';
import { ASSET_CHANGE_REASON_ORDER } from 'constants/asset-balance-history.constants';
import { useQueryFilters } from 'hooks';
import { TranslationNamespace } from 'i18n';
import {
  AssetBalanceHistory,
  AssetBalanceHistoryFilters,
  FilterDefinition,
  PaginatedData,
} from 'types';
import { assetBalanceHistoryUtils, filtersUtils } from 'utils';

type Props = {
  queryResult: UseQueryResult<PaginatedData<AssetBalanceHistory>>;
  hideTitle?: boolean;
  additionalColumns?: DataGridColumnDefinition<AssetBalanceHistory>[];
  additionalFilters?: FilterDefinition<AssetBalanceHistoryFilters>[];
};

export const BalanceHistory: React.FC<Props> = ({
  queryResult,
  hideTitle,
  additionalColumns = [],
  additionalFilters = [],
}) => {
  const { t } = useTranslation(TranslationNamespace.Admin, {
    keyPrefix: 'pages.asset_balance_history',
  });

  const { filters } = useQueryFilters<AssetBalanceHistoryFilters>(
    assetBalanceHistoryUtils.getDefaultFilters(),
  );

  const formatItems = useCallback(
    (items: AssetBalanceHistory[]) =>
      orderBy(
        items,
        [
          'createdAt',
          'orderId',
          (item) => -indexOf(ASSET_CHANGE_REASON_ORDER, item.assetChangeReason),
        ],
        ['desc', 'asc', 'desc'],
      ),
    [],
  );

  return (
    <CrudPage
      header={{ hide: hideTitle, title: t('title') }}
      filters={{
        filtersDefinitions: [
          ...filtersUtils.getCommonFilters(filters),
          ...additionalFilters,
          ...assetBalanceHistoryUtils.getCommonFilters(),
        ],
      }}
      table={{
        queryResult,
        formatItems,
        paginated: true,
        columns: [
          ...assetBalanceHistoryUtils.getCommonColumns(),
          ...additionalColumns,
        ],
      }}
    />
  );
};
